import type { Placement } from './types';

/** Габариты зоны печати на экране, в пикселях. */
export interface AreaRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Нормализованное положение → пиксели для отрисовки. */
export function toPixels(placement: Placement, area: AreaRect) {
  return {
    left: area.left + placement.x * area.width,
    top: area.top + placement.y * area.height,
    width: placement.width * area.width,
    height: placement.height * area.height,
  };
}

/** Пиксели → нормализованное положение для хранения и отправки на сервер. */
export function toNormalized(
  px: { left: number; top: number; width: number; height: number },
  area: AreaRect,
): Omit<Placement, 'rotation'> {
  return {
    x: (px.left - area.left) / area.width,
    y: (px.top - area.top) / area.height,
    width: px.width / area.width,
    height: px.height / area.height,
  };
}

/** Не выпускаем принт за safe-zone. Габариты сохраняются, сдвигается позиция. */
export function clampToArea(placement: Placement): Placement {
  const width = Math.min(placement.width, 1);
  const height = Math.min(placement.height, 1);
  return {
    ...placement,
    width,
    height,
    x: clamp(placement.x, 0, 1 - width),
    y: clamp(placement.y, 0, 1 - height),
  };
}

/**
 * Масштабирование с сохранением пропорций.
 * Пропорции залочены по умолчанию: искажённый логотип артиста лейбл не примет.
 */
export function resizeKeepingRatio(
  placement: Placement,
  nextWidth: number,
  aspect: number,
  areaAspect: number,
): Placement {
  const width = clamp(nextWidth, MIN_SIZE, 1);
  // Зона печати не квадратная, поэтому высоту пересчитываем через её пропорции.
  const height = (width / aspect) * areaAspect;
  return clampToArea({ ...placement, width, height });
}

export const MIN_SIZE = 0.05;
/** Порог прилипания к центру и краям, в долях зоны. */
export const SNAP_THRESHOLD = 0.012;

/** Прилипание к центру по горизонтали и вертикали. */
export function snap(placement: Placement): Placement {
  const centerX = 0.5 - placement.width / 2;
  const centerY = 0.5 - placement.height / 2;
  return {
    ...placement,
    x: Math.abs(placement.x - centerX) < SNAP_THRESHOLD ? centerX : placement.x,
    y: Math.abs(placement.y - centerY) < SNAP_THRESHOLD ? centerY : placement.y,
  };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/**
 * Пропорции зоны печати по умолчанию — у футболки.
 *
 * Для остальных изделий значение берётся из реестра функцией zoneAspect:
 * у шоппера зона почти квадратная, у кепки вытянутая вдвое, и стартовая
 * рамка принта обязана это учитывать.
 */
export const ZONE_ASPECT = 0.7136;

/**
 * Стартовое положение нового принта: по центру, почти во всю зону.
 *
 * Высота считается из пропорций самого принта, а не берётся квадратом.
 * Иначе у горизонтального принта рамка выше картинки, сверху и снизу
 * остаются пустые поля, и принт нельзя подвинуть вплотную к краю зоны:
 * упирается не изображение, а пустота вокруг него.
 */
export function defaultPlacement(aspect: number, areaAspect: number = ZONE_ASPECT): Placement {
  let width = 0.88;
  let height = (width / aspect) * areaAspect;

  // Высокий принт не должен вылезать за зону: уменьшаем обе стороны разом,
  // иначе обрезка по высоте исказит пропорции.
  if (height > 1) {
    width *= 1 / height;
    height = 1;
  }

  return clampToArea({
    x: (1 - width) / 2,
    y: (1 - height) / 2,
    width,
    height,
    rotation: 0,
    flipped: false,
  });
}
